import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import Cookies from "js-cookie";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs/tabs";
import { Form } from "@/components/ui/form/form";

import { createNewEmployee } from "@/config/mutation/admin";
import { NewEmployeeOverview } from "./comps/new-emp-overview";
import { NewEmployeeAddressContacts } from "./comps/new-emp-address-contacts";
import { NewEmployeeAttendanceLeaves } from "./comps/new-emp-attendance-leaves";
import { NewEmployeeSalary } from "./comps/new-emp-salary";
import { NewEmployeePersonal } from "./comps/new-emp-personal";
import { NewDocuments } from "./comps/new-documents";

const formSchema = z.object({
  // ? TAB Overview
  name: z
    .string({
      required_error: "Employee name required!",
    })
    .min(1, {
      message: "Employee name is required!",
    }),
  email: z
    .string({
      required_error: "Email is required!",
    })
    .email({
      message: "Invalid Email",
    }),
  password: z
    .string({
      required_error: "Password required!",
    })
    .min(1, {
      message: "Password is required!",
    }),

  role: z
    .string({
      required_error: "Employee role required!",
    })
    .min(1, {
      message: "Employee role is required!",
    }),
  photo: z.string().optional(),

  // ? TAB Address & Contacts
  address: z.string().optional(),
  phone: z.string().length(10, {
    message: "Phone number should of length 10 digit",
  }),
  emergencyContactNumber: z.string().length(10, {
    message: "Phone number should of length 10 digit",
  }),

  // ? TAB Attendance & Leaves
  holidayList: z.string().optional(),
  paidLeavesTotal: z.coerce.number(),
  paidLeavesRemaining: z.coerce.number().optional(),
  leaveApprover: z.string().optional(),
  allowanceApprover: z.string().optional(),
  loanApprover: z.string().optional(),

  // ? TAB Salary
  grossSalary: z.coerce.number().optional(),
  travellingExpenses: z.coerce.number().optional(),
  otherExpenses: z.coerce.number().optional(),
  paidAmount: z.coerce.number().optional(),

  // ? TAB Personal
  bloodGroup: z.string().optional(),
  employeeCode: z.string().optional(),
  panNumber: z.string().optional(),
  pfNumber: z.string().optional(),
  otherDetails: z.string().optional(),
  familyDetails: z.string().optional(),

  // ? TAB Profile
  offerLetter: z.string().optional(),
  promotionLetters: z.array(z.string()).optional(),
  officeRulesHandbook: z.array(z.string()).optional(),

  // ? TAB Documents
  documents: z
    .array(
      z.object({
        doc: z.string(),
        url: z.string(),
      })
    )
    .optional(),
});

export type FormValues = z.infer<typeof formSchema>;

export const NewEmployeeForm = () => {
  const navigate = useNavigate();
  const adminKey = Cookies.get("key") as string;
  const queryClient = useQueryClient();

  // 1. Define your form.
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      // ? TAB Overview
      name: "",
      email: "",
      password: "",
      role: "",
      photo: "",

      // ? TAB Address & Contacts
      phone: "",
      emergencyContactNumber: "",
      address: "",

      // ? TAB Attendance & Leaves
      holidayList: "",
      paidLeavesTotal: 0,
      paidLeavesRemaining: 0,
      leaveApprover: "",
      allowanceApprover: "",
      loanApprover: "",

      // ? TAB Salary
      grossSalary: 0,
      travellingExpenses: 0,
      otherExpenses: 0,
      paidAmount: 0,

      // ? TAB Personal
      bloodGroup: "",
      employeeCode: "",
      panNumber: "",
      pfNumber: "",
      otherDetails: "",
      familyDetails: "",

      // ? TAB Profile
      offerLetter: "",
      promotionLetters: [],
      officeRulesHandbook: [],

      documents: [],
    },
  });

  // console.log(form.formState.errors);

  const { mutate, isPending } = useMutation({
    mutationFn: createNewEmployee(adminKey),
  });

  // 2. Define a submit handler.
  function onSubmit(values: FormValues) {
    // console.log(values);

    mutate(
      {
        name: values.name,
        email: values.email,
        password: values.password,
        phone: values.phone,
        role: values.role,
        leaveApprover:
          values.leaveApprover === "" ? null : values.leaveApprover,
        allowanceApprover:
          values.allowanceApprover === "" ? null : values.allowanceApprover,
        loanApprover: values.loanApprover === "" ? null : values.loanApprover,
        grossSalary: values.grossSalary,
        travellingExpenses: values.travellingExpenses,
        otherExpenses: values.otherExpenses,
        photo: values?.photo,
        emergencyContactNumber: values.emergencyContactNumber,
        bloodGroup: values.bloodGroup,
        address: values.address,
        offerLetter: "",
        promotionLetters: [],
        officeRulesHandbook: [],
        paidLeavesTotal: values.paidLeavesTotal,
        paidLeavesRemaining: values.paidLeavesRemaining,
        documents: values?.documents,
        holidayList: values?.holidayList === "" ? null : values?.holidayList,
        employeeCode: values?.employeeCode,
        panNumber: values?.panNumber,
        pfNumber: values?.pfNumber,
        paidAmount: values?.paidAmount,
        otherDetails: values?.otherDetails,
        familyDetails: values?.familyDetails,
      },
      {
        onSuccess: async () => {
          // console.log("New Employee", data);
          await queryClient.invalidateQueries({
            queryKey: ["employees"],
          });

          toast.success("New Employee Created!");

          navigate("/admin/employees", {
            replace: true,
          });
        },

        onError: (error) => {
          console.log("ERROR WHILE CREATING NEW EMPLOYEE", error);
          const isAxiosError = error instanceof AxiosError;
          if (isAxiosError) {
            const errorReason = error?.response?.data?.error as string;
            toast.error(errorReason);
          } else {
            toast.error(error?.message);
          }
        },
      }
    );
  }

  return (
    <div className="p-5">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-medium">New Employee</h2>

            <Button type="submit" disabled={isPending} size="sm">
              Continue
            </Button>
          </div>

          <div className="mt-5">
            <Tabs defaultValue="overview" className="mr-auto w-full">
              <div className="flex items-center justify-between pb-3 sticky top-[56px] bg-card z-30">
                <TabsList className="w-full justify-start rounded-none border-b border-b-gray-500 bg-transparent p-0">
                  <TabsTrigger
                    value="overview"
                    className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Overview
                  </TabsTrigger>

                  <TabsTrigger
                    value="address-contacts"
                    className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Address & Contacts
                  </TabsTrigger>
                  <TabsTrigger
                    value="attendance-leaves"
                    className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Attendance & Leaves
                  </TabsTrigger>
                  <TabsTrigger
                    value="salary"
                    className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Salary
                  </TabsTrigger>
                  <TabsTrigger
                    value="personal"
                    className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Personal
                  </TabsTrigger>
                  <TabsTrigger
                    value="documents"
                    className="relative h-9 rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-semibold text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Documents
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview" className="relative rounded-md">
                <NewEmployeeOverview isPending={isPending} />
              </TabsContent>
              <TabsContent
                value="address-contacts"
                className="relative rounded-md"
              >
                <NewEmployeeAddressContacts isPending={isPending} />
              </TabsContent>
              <TabsContent
                value="attendance-leaves"
                className="relative rounded-md"
              >
                <NewEmployeeAttendanceLeaves isPending={isPending} />
              </TabsContent>
              <TabsContent value="salary" className="relative rounded-md">
                <NewEmployeeSalary isPending={isPending} />
              </TabsContent>
              <TabsContent value="personal" className="relative rounded-md">
                <NewEmployeePersonal isPending={isPending} />
              </TabsContent>
              <TabsContent value="documents" className="relative rounded-md">
                <NewDocuments isPending={isPending} />
              </TabsContent>
            </Tabs>
          </div>
        </form>
      </Form>
    </div>
  );
};
